import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Mail, Send, Sparkles } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Seo } from '@/client/components/Seo';
import { api, ApiError } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { isLocale, type Locale } from '@/client/i18n';
import { TurnstileWidget } from '@/client/features/contact/TurnstileWidget';

type ConfigResponse = { turnstileSiteKey: string | null };

const TOPICS = ['course', 'consultation', 'cooperation', 'other'] as const;
type Topic = (typeof TOPICS)[number];

export function Contact() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [topic, setTopic] = useState<Topic>('course');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const config = useQuery<ConfigResponse>({
    queryKey: ['public', 'config'],
    queryFn: () => api<ConfigResponse>('/api/v1/config'),
    staleTime: 1000 * 60 * 60,
  });

  const submit = useMutation({
    mutationFn: () =>
      api('/api/v1/contact', {
        method: 'POST',
        body: {
          name,
          email,
          phone: phone || null,
          topic,
          message,
          turnstileToken: token,
        },
      }),
    onSuccess: () => {
      setSubmitted(true);
      setErrorMsg(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setErrorMsg(t('contact.error_rate_limited'));
        } else if (err.code === 'VALIDATION_FAILED') {
          setErrorMsg(t('contact.error_turnstile_or_validation'));
          setToken(null);
        } else {
          setErrorMsg(t('errors.generic'));
        }
      } else {
        setErrorMsg(t('errors.generic'));
      }
    },
  });

  const seoBlock = (
    <Seo
      title={t('seo.contact_title')}
      description={t('seo.contact_description')}
      locale={locale}
    />
  );

  // Graceful degradation: if the server config endpoint hasn't been provisioned
  // with a Turnstile site key yet, fall back to a mailto card so visitors still
  // have a path to reach us. Auto-flips back to the form once the secret lands.
  if (config.isSuccess && !config.data?.turnstileSiteKey) {
    return (
      <article>
        {seoBlock}
        <section className="relative mx-auto max-w-2xl px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div
            className="absolute top-20 -left-20 w-[24rem] h-[24rem] rounded-full pointer-events-none animate-float-orb"
            style={{
              background: 'radial-gradient(circle, rgba(193,167,226,0.35) 0%, transparent 65%)',
              filter: 'blur(40px)',
            }}
            aria-hidden
          />
          <div className="relative dr-card-strong p-10 sm:p-14 text-center">
            <div className="relative mx-auto h-24 w-24 mb-6">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    'conic-gradient(from 220deg, rgba(193,167,226,0.40), rgba(117,59,189,0.18), rgba(193,167,226,0.40))',
                  filter: 'blur(8px)',
                }}
                aria-hidden
              />
              <img
                src="/brand/mascot-purple.svg"
                alt=""
                className="relative h-full w-full object-contain animate-float"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = '/brand/dajana-purple.svg';
                }}
              />
            </div>
            <h1 className="font-display text-h1 text-primary-dark">{t('contact.page_title')}</h1>
            <p className="mt-4 font-display text-body-large text-text-secondary leading-relaxed max-w-md mx-auto">
              {t('contact.fallback_body')}
            </p>
            <a
              href="mailto:hello@dajanarodriguez.com"
              className="mt-8 inline-flex items-center gap-2 rounded-pill bg-gradient-to-br from-primary to-primary-dark text-white px-6 h-12 font-sans text-body-small font-medium shadow-[0_4px_16px_rgba(117,59,189,0.28)] hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(117,59,189,0.38)] transition-all"
            >
              <Mail size={16} />
              hello@dajanarodriguez.com
            </a>
          </div>
        </section>
      </article>
    );
  }

  if (submitted) {
    return (
      <article>
        {seoBlock}
        <section className="relative mx-auto max-w-2xl px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div
            className="absolute top-20 -right-20 w-[24rem] h-[24rem] rounded-full pointer-events-none animate-float-orb"
            style={{
              background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
              filter: 'blur(40px)',
            }}
            aria-hidden
          />
          <div className="relative dr-card-strong p-10 sm:p-14 text-center animate-fade-up">
            <div className="relative mx-auto h-24 w-24 mb-6">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    'conic-gradient(from 220deg, rgba(45,139,85,0.30), rgba(193,167,226,0.30), rgba(45,139,85,0.30))',
                  filter: 'blur(8px)',
                }}
                aria-hidden
              />
              <img
                src="/brand/mascot-purple.svg"
                alt=""
                className="relative h-full w-full object-contain animate-float"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = '/brand/dajana-purple.svg';
                }}
              />
            </div>
            <div className="inline-flex items-center gap-2 rounded-pill bg-success/10 border border-success/25 text-success px-3 py-1 font-sans text-caption font-medium">
              <CheckCircle2 size={12} />
              {locale === 'cs' ? 'Odesláno' : 'Odoslané'}
            </div>
            <h1 className="mt-4 font-display text-h2 text-primary-dark">
              {t('contact.success_title')}
            </h1>
            <p className="mt-3 font-display text-body-large text-text-secondary leading-relaxed max-w-md mx-auto">
              {t('contact.success_body')}
            </p>
          </div>
        </section>
      </article>
    );
  }

  return (
    <article>
      {seoBlock}
      <section className="relative">
        <div
          className="absolute top-10 -right-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div
          className="absolute top-40 -left-20 w-[24rem] h-[24rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(117,59,189,0.18) 0%, transparent 65%)',
            filter: 'blur(48px)',
            animationDelay: '-10s',
          }}
          aria-hidden
        />

        <div className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-6">
          <p className="dr-eyebrow animate-fade-up">{t('contact.eyebrow', { defaultValue: 'Napíš nám' })}</p>
          <h1
            className="mt-3 font-display text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
            style={{ animationDelay: '70ms' }}
          >
            {t('contact.page_title')}
          </h1>
          <p
            className="mt-4 font-display text-body-large text-text-secondary leading-relaxed max-w-2xl animate-fade-up"
            style={{ animationDelay: '140ms' }}
          >
            {t('contact.page_intro')}
          </p>
        </div>

        <div className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 pb-20">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!token) {
                setErrorMsg(t('contact.error_turnstile_required'));
                return;
              }
              submit.mutate();
            }}
            className="dr-card-static p-6 sm:p-10 space-y-5 animate-fade-up"
            style={{ animationDelay: '210ms' }}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="contact-name">{t('contact.label_name')}</Label>
                <Input
                  id="contact-name"
                  required
                  minLength={2}
                  maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="contact-email">{t('contact.label_email')}</Label>
                <Input
                  id="contact-email"
                  type="email"
                  required
                  maxLength={200}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="contact-phone">{t('contact.label_phone')}</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  maxLength={50}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>{t('contact.label_topic')}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TOPICS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setTopic(opt)}
                    className={cn(
                      'rounded-pill px-4 h-10 font-sans text-body-small font-medium transition-all border',
                      topic === opt
                        ? 'bg-gradient-to-br from-primary to-primary-dark text-white border-transparent shadow-[0_2px_10px_rgba(117,59,189,0.25)]'
                        : 'bg-white/70 backdrop-blur-md border-glass-border-strong text-text-secondary hover:border-primary/30 hover:text-primary',
                    )}
                  >
                    {t(`contact.topic_${opt}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="contact-message">{t('contact.label_message')}</Label>
              <textarea
                id="contact-message"
                required
                minLength={10}
                maxLength={2000}
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="block w-full rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong p-4 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
              />
              <p className="mt-1.5 font-sans text-caption text-text-muted text-right">
                {message.length} / 2000
              </p>
            </div>

            <div>
              <TurnstileWidget
                siteKey={config.data?.turnstileSiteKey ?? null}
                onToken={setToken}
                onExpired={() => setToken(null)}
              />
            </div>

            {errorMsg && (
              <p
                className="rounded-2xl px-4 py-3 font-sans text-body-small text-error border"
                style={{ background: 'rgba(196,50,45,0.08)', borderColor: 'rgba(196,50,45,0.25)' }}
              >
                {errorMsg}
              </p>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between pt-2">
              <p className="font-sans text-caption text-text-muted inline-flex items-start gap-1.5">
                <Sparkles size={11} className="text-primary mt-0.5 shrink-0" />
                {t('contact.disclaimer', {
                  defaultValue: 'Ozveme sa zvyčajne do 48 hodín.',
                })}
              </p>
              <Button type="submit" disabled={submit.isPending}>
                {submit.isPending ? t('common.loading') : t('contact.submit_button')}
                <Send size={16} />
              </Button>
            </div>
          </form>
        </div>
      </section>
    </article>
  );
}
